import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View
} from "react-native";

import { Backdrop } from "@/components/Artwork";
import { palette } from "@/design/tokens";

/**
 * Poster veil for the player box. While a dance's video has no rendered
 * frames yet — waiting on the playback grant or on the stream's first
 * paint — the dance's own artwork fills the frame as a soft glow instead of
 * a black rectangle. It dissolves the moment real frames exist, and returns
 * with the next dance's artwork on queue changes.
 *
 * The spinner holds back for a beat so warm starts never blink one.
 */

const useNative = Platform.OS !== "web";
const SPINNER_DELAY_MS = 400;

export function PlayerVeil({
  uri,
  fallbackSeed,
  visible,
  danceId,
  showSpinner = true
}: {
  uri: string | null;
  fallbackSeed: string;
  visible: boolean;
  danceId: string | null;
  showSpinner?: boolean;
}) {
  const opacity = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const shownAt = useRef(visible ? Date.now() : 0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingAttempt = useRef<{
    danceId: string | null;
    startedAt: number;
  } | null>(visible ? { danceId, startedAt: Date.now() } : null);
  const [mounted, setMounted] = useState(visible);
  const [waitedABeat, setWaitedABeat] = useState(false);

  useEffect(() => {
    console.log(`[veil] ${visible ? "show" : "dismiss"} dance=${danceId ?? "none"}`);
  }, [danceId, visible]);

  useEffect(() => {
    if (!visible || !showSpinner) {
      setWaitedABeat(false);
      return undefined;
    }

    const now = Date.now();
    if (!loadingAttempt.current || loadingAttempt.current.danceId !== danceId) {
      loadingAttempt.current = { danceId, startedAt: now };
    }
    const remainingDelay = Math.max(
      0,
      SPINNER_DELAY_MS - (now - loadingAttempt.current.startedAt)
    );
    setWaitedABeat(false);
    if (remainingDelay === 0) {
      setWaitedABeat(true);
      return undefined;
    }
    const timer = setTimeout(() => setWaitedABeat(true), remainingDelay);
    return () => clearTimeout(timer);
  }, [danceId, showSpinner, visible]);

  useEffect(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (visible) {
      setMounted(true);
      opacity.stopAnimation();
      opacity.setValue(1);
      shownAt.current = Date.now();
      return;
    }
    const fadeOut = () => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: useNative
      }).start(({ finished }) => {
        if (finished) {
          loadingAttempt.current = null;
          setWaitedABeat(false);
        }
        // Native keeps this transition surface composed behind VideoView.
        // Re-inserting it while simultaneously concealing AVPlayer can expose
        // one frame of the player's black base before the artwork arrives.
        if (finished && !useNative) setMounted(false);
      });
    };
    const remainingHold = Math.max(0, 250 - (Date.now() - shownAt.current));
    if (remainingHold > 0) hideTimer.current = setTimeout(fadeOut, remainingHold);
    else fadeOut();
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [danceId, opacity, visible]);

  // `visible` must win during render, before effects run. Queue changes can
  // otherwise paint one frame of the outgoing VideoView while `mounted` is
  // still false from the previous fade.
  if (!useNative && !mounted && !visible) return null;

  return (
    <Animated.View
      style={[styles.veil, { opacity: visible ? 1 : opacity, pointerEvents: "none" }]}
      testID="player-veil"
    >
      {useNative ? (
        <LinearGradient
          colors={["#274468", "#131A2C"]}
          style={StyleSheet.absoluteFill}
          testID="player-veil-resident-base"
        />
      ) : null}
      {Platform.OS === "web" ? (
        <Backdrop uri={uri} fallbackSeed={fallbackSeed} intensity={0.9} />
      ) : (
        // The same 42px raster blur is strong in the short now-playing bar
        // but reads sharp at full-player size. Compose at that smaller scale,
        // then enlarge the already blurred Backdrop result to fill the veil.
        <View style={styles.nativeBackdropRaster}>
          <Backdrop uri={uri} fallbackSeed={fallbackSeed} intensity={0.9} />
        </View>
      )}
      {/* Mirror the title's top-edge scrim while loading. Without this lower
          anchor, the centered spinner reads optically high even when its
          geometry is correct. Keeping it inside the veil makes it loading-
          only and identical for Video.js and expo-video. */}
      <LinearGradient
        colors={["rgba(0,0,0,0)", "rgba(0,0,0,0.22)", "rgba(0,0,0,0.55)"]}
        locations={[0, 0.45, 1]}
        style={[styles.bottomScrim, { pointerEvents: "none" }]}
        testID="player-veil-bottom-scrim"
      />
      <View style={styles.spinnerSlot}>
        {showSpinner && waitedABeat && visible ? (
          <ActivityIndicator color={palette.accent} />
        ) : null}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  veil: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    backgroundColor: palette.playerBg,
    justifyContent: "center",
    elevation: 20,
    zIndex: 20
  },
  spinnerSlot: {
    height: 36,
    justifyContent: "center"
  },
  nativeBackdropRaster: {
    height: "25%",
    left: "37.5%",
    position: "absolute",
    top: "37.5%",
    transform: [{ scale: 4 }],
    width: "25%"
  },
  bottomScrim: {
    bottom: 0,
    // Video.js's content-sized title scrim settles around 85px; native's
    // PlayerChrome title scrim is explicitly 110px.
    height: Platform.OS === "web" ? 85 : 110,
    left: 0,
    position: "absolute",
    right: 0
  }
});
